<template>
    <div>
        <Panel id="audit_log_filters_panel" :title="$t('Filters')">
            <div id="audit_log_filters">
                <Stack direction="row" gap="1rem">
                    <DatePicker
                        id="from-date-wrapper"
                        :label="`${$t('Date From')}:`"
                        :value="startDate"
                        input-id="fromDate"
                        popper-class="from-date-popper"
                        type="datetime"
                        @input="(val) => (startDate = val)"
                    />
                    <DatePicker
                        id="to-date-wrapper"
                        :label="`${$t('Date To')}:`"
                        :value="endDate"
                        input-id="toDate"
                        popper-class="to-date-popper"
                        type="datetime"
                        @input="(val) => (endDate = val)"
                    />
                </Stack>
                <Stack direction="row" gap="1rem">
                    <SelectInput
                        :label="`${$t('Product')}:`"
                        :loading="$store.isLoading('auditlog/filters')"
                        :options="productOptions"
                        :placeholder="$t('All')"
                        :value="filterProducts"
                        input-id="product_filter"
                        multiple
                        @input="(val) => (filterProducts = val)"
                    />
                    <SelectInput
                        :label="`${$t('Operation')}:`"
                        :options="[
                            { value: 'Login', label: $t('Login') },
                            { value: 'Logout', label: $t('LOGOUT') },
                            { value: 'Created', label: $t('Created') },
                            { value: 'Updated', label: $t('Updated') },
                            { value: 'Deleted', label: $t('Deleted') },
                        ]"
                        :placeholder="$t('All')"
                        :value="filterOperation"
                        clearable
                        input-id="operation_filter"
                        @input="(val) => (filterOperation = val)"
                    />
                    <SelectInput
                        :label="`${$t('User')}:`"
                        :loading="$store.isLoading('auditlog/filters')"
                        :options="userOptions"
                        :placeholder="$t('All')"
                        :value="filterUser"
                        clearable
                        input-id="user_filter"
                        @input="(val) => (filterUser = val)"
                    />
                    <TextField
                        :label="`${$t('Name')}:`"
                        :value="filterName"
                        input-id="name_filter"
                        @input="(val) => (filterName = val)"
                    />
                </Stack>
            </div>
            <template #footer>
                <TextButton id="search_button" @click="loadTable(1)">{{ $t('Search') }}</TextButton>
            </template>
        </Panel>
        <Panel
            id="audit_log_records"
            :title="$t('Audit Log')"
            :page="page"
            :total-rows="totalRows"
            @change="changePage"
        >
            <table id="auditlog_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('User') }}</th>
                        <th>{{ $t('Organisation') }}</th>
                        <th>{{ $t('Product') }}</th>
                        <th>{{ $t('Description') }}</th>
                        <th>{{ $t('Date') }}</th>
                        <template v-if="$store.hasRight('IMPERSONATE_USER_AUDIT')">
                            <th>{{ $t('Impersonator User') }}</th>
                            <th>{{ $t('Impersonator Organisation') }}</th>
                        </template>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="log in logs">
                        <tr>
                            <td>{{ log.szUsername }}</td>
                            <td>{{ log.szOrganisationName }}</td>
                            <td>{{ log.productTrans }}</td>
                            <td
                                v-if="
                                    $store.hasRight('VIEW_ROUTE_PLANS') &&
                                    log.product === 7 &&
                                    log.changes.some((x) => x.name === 'revision') &&
                                    log.description === 'route plan \'<identifier.name>\' updated'
                                "
                                class="route-plan-change"
                                @click="openRevisions(log)"
                            >
                                {{ log.formatted }}
                            </td>
                            <td
                                v-else-if="log.product === 7 && log.changes.length > 0"
                                :title="getChanges(log.changes)"
                            >
                                {{ log.formatted }}
                            </td>
                            <td v-else>
                                {{ log.formatted }}
                            </td>
                            <td>{{ log.dtDateTime }}</td>
                            <template v-if="$store.hasRight('IMPERSONATE_USER_AUDIT')">
                                <td>{{ log.impersonatorUser }}</td>
                                <td>{{ log.impersonatorOrg }}</td>
                            </template>
                            <td>
                                <IconButton
                                    v-if="expanded.includes(log.logId)"
                                    icon="up-icon"
                                    @click="expanded.splice(expanded.indexOf(log.logId), 1)"
                                />
                                <IconButton
                                    v-else-if="Object.keys(log.next).length > 0"
                                    icon="down-icon"
                                    @click="expanded.push(log.logId)"
                                />
                            </td>
                        </tr>
                        <tr v-if="expanded.includes(log.logId)">
                            <td colspan="8">
                                <div
                                    v-for="logKey in Object.keys(log.next).filter((k) => k !== 'szSessionToken')"
                                    class="audit-next-row"
                                >
                                    <AuditLogField :name="logKey" />
                                    <AuditLogValue :name="logKey" :value="log.next[logKey]" />
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <template #footer>
                <TextButton
                    id="export_button"
                    :disabled="totalRows === 0 || totalRows > 1000"
                    :title="
                        totalRows > 1000
                            ? $t('The maximum number of logs that can be exported at a time is 1000')
                            : undefined
                    "
                    @click="handleExport"
                >
                    {{ $t('Export') }}
                </TextButton>
            </template>
        </Panel>

        <CicPopup v-if="plan" id="audit_revisions" :draggable="false" :title="plan.new.name" @blur="plan = null">
            <EditRoutePlanInner id="plan_old" :item="plan.previous" />
            <EditRoutePlanInner id="plan_new" :item="plan.new" />
        </CicPopup>

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import moment from 'moment';
    import { CicPopup, Panel, Stack } from '@/components/container';
    import { IconButton, TextButton } from '@/components/controls';
    import { DatePicker, SelectInput, TextField } from '@/components/inputs';
    import EditRoutePlanInner from '@/feature/inbound/routePlans/EditRoutePlanInner';
    import { load, post } from '@/utils/Http';
    import { loadInitialPrompts } from '@/utils/RouteCollectionUtils';
    import AuditLogField from './AuditLogField';
    import AuditLogValue from './AuditLogValue';
    import './styles/auditlog.css';

    export default {
        name: 'AuditLog',
        components: {
            AuditLogField,
            AuditLogValue,
            CicPopup,
            DatePicker,
            EditRoutePlanInner,
            IconButton,
            Panel,
            SelectInput,
            Stack,
            TextButton,
            TextField,
        },
        data() {
            return {
                expanded: [],
                filterName: null,
                filterOperation: null,
                filterProducts: [],
                filterUser: null,
                logs: [],
                page: 1,
                productOptions: [],
                userOptions: [],
                totalRows: 0,
                startDate: moment(moment().format('YYYY-MM-DD 00:00:00')),
                endDate: moment(moment().format('YYYY-MM-DD 23:59:59')),
                plan: null,
                store: null,
            };
        },
        created() {
            this.loadTable();
            this.loadFilters();
        },
        methods: {
            buildPlan(log, instance, response) {
                const nameChange = log.changes.find((change) => change.name === 'name');
                const revChange = log.changes.find((change) => change.name === 'revision');
                const useChange = log.changes.find((change) => change.name === 'reusable');
                return {
                    planId: log.id,
                    name: nameChange ? nameChange[instance] : log.name,
                    revision: revChange[instance],
                    reusable: useChange && useChange[instance] === '1',
                    rejectCLIR: response.rejectCLIR,
                    withholdCLI: response.withholdCLI,
                    route: response.revision,
                    voiceOnly: true,
                };
            },

            changePage(page) {
                this.page = page;
                this.loadTable();
            },

            formatChangeValue(name, val) {
                if (name === 'CLI_GROUP' && val === 'Default') {
                    return this.$t('Default');
                }

                if (name === 'OBJECT_PROFILE' && val === 'All') {
                    return this.$t('All');
                }

                return val;
            },

            getChanges(changes) {
                return changes
                    .map((change) => {
                        const previous = this.formatChangeValue(change.name, change.previous);
                        const newVal = this.formatChangeValue(change.name, change.new);

                        return `${this.$t(change.name)} : ${previous} -> ${newVal}`;
                    })
                    .join('\r\n');
            },

            handleExport() {
                const params = {
                    startDate: this.startDate.format('YYYY-MM-DD HH:mm:ss'),
                    endDate: this.endDate.format('YYYY-MM-DD HH:mm:ss'),
                    products: JSON.stringify(this.filterProducts),
                    userId: this.filterUser ?? null,
                    name: this.filterName,
                    operation: this.filterOperation,
                };
                this.$store.exportFile({ link: 'auditlog/export', params });
            },

            loadFilters() {
                post('auditlog/filters', {}, ({ products, users }) => {
                    this.productOptions = [];
                    if (!products.includes('ServicePortal')) {
                        this.productOptions.push({ value: 'ServicePortal', label: this.$t('ServicePortal') });
                    }
                    this.productOptions = this.productOptions.concat(
                        products.map((product) => ({ value: product, label: this.$t(product) })),
                    );
                    this.productOptions.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

                    this.userOptions = users.map((user) => ({
                        value: user.userId,
                        label: user.username,
                    }));
                });
            },

            // Load table of search results
            loadTable(search = 0) {
                if (!moment.isMoment(this.startDate)) {
                    alert(this.$t('Invalid Date From'));
                    return;
                }

                if (!moment.isMoment(this.endDate) || this.endDate.isBefore(this.startDate)) {
                    alert(this.$t('Invalid Date To'));
                    return;
                }

                if (search) {
                    this.page = 1;
                }

                let params = {
                    page: this.page,
                    startDate: this.startDate.format('YYYY-MM-DD HH:mm:ss'),
                    endDate: this.endDate.format('YYYY-MM-DD HH:mm:ss'),
                    products: JSON.stringify(this.filterProducts),
                    userId: this.filterUser ?? null,
                    name: this.filterName ?? null,
                    operation: this.filterOperation ?? null,
                };
                this.logs = [];
                load('auditlog/get', params, (response) => {
                    this.logs = response.logs;
                    this.totalRows = response.totalRows;
                });
            },

            openRevisions(log) {
                this.plan = null;
                this.$store.clearRouteData();

                const plan = { previous: {}, new: {} };
                const revChange = log.changes.find((change) => change.name === 'revision');

                const params = { planId: log.id, revisionId: revChange.previous, services: 1 };

                load('routeplan/revision', params, (response) => {
                    this.$store.buildRouteStore(response);
                    loadInitialPrompts(response.revision, load);
                    plan.previous = this.buildPlan(log, 'previous', response);

                    const params2 = { planId: log.id, revisionId: revChange.new };

                    load('routeplan/revision', params2, (response) => {
                        loadInitialPrompts(response.revision, load);
                        plan.new = this.buildPlan(log, 'new', response);
                        this.plan = plan;
                    });
                });
            },
        },
    };
</script>
